const blankRoommate = () => ({ name: "", rent: "", deposit: "" });

const defaults = (rentFrequency) => ({
  address: "",
  landlord: "",
  startDate: "",
  endDate: "",
  furnishing: "unfurnished",
  roommates: [blankRoommate()],
  rentFrequency,
  rentDueDay: "",
  rentPaidTo: "",
  bills: {
    electricity: true,
    gas: true,
    water: true,
    internet: true,
    streaming: false,
  },
  billsSplit: "equal",
  rules: {
    cleaning: { enabled: false, method: "roster" },
    food: { enabled: false, arrangement: "separate" },
    quietHours: { enabled: false, from: "22:00", to: "07:00" },
    guests: { enabled: false, nightsPerWeek: 3 },
    pets: { enabled: false, policy: "no" },
    smoking: { enabled: false, policy: "outside" },
    notice: { enabled: true, weeks: 4 },
  },
  additionalTerms: "",
});

const sentenceList = (items) =>
  items.length > 1 ? `${items.slice(0, -1).join(", ")} and ${items.at(-1)}` : items.join("");

export default function ({ currencySymbol = "$", defaultRentFrequency = "per month" } = {}) {
  return {
    currencySymbol,
    defaultRentFrequency,
    agreement: this.$persist(defaults(defaultRentFrequency)).as("roommate-agreement"),

    get names() {
      return sentenceList(
        this.agreement.roommates.map((roommate, index) => this.nameFor(roommate, index)),
      );
    },

    get introduction() {
      const { address, landlord, startDate, endDate } = this.agreement;

      return [
        "This agreement is between ",
        landlord.trim() && `${landlord.trim()} (the landlord) and `,
        this.names,
        address.trim() && ` for the home at ${address.trim()}`,
        startDate && `, starting ${this.date(startDate)}`,
        endDate && ` and ending ${this.date(endDate)}`,
        ".",
      ]
        .filter(Boolean)
        .join("");
    },

    get includedBills() {
      return sentenceList(
        Object.entries(this.agreement.bills)
          .filter(([, included]) => included)
          .map(([bill]) => bill),
      );
    },

    get totalRent() {
      return this.sum("rent");
    },

    get totalDeposit() {
      return this.sum("deposit");
    },

    get rentSummary() {
      const { rentFrequency, rentDueDay, rentPaidTo } = this.agreement;
      const dueDay = rentDueDay.trim();

      return [
        `Total rent is ${this.money(this.totalRent)} ${rentFrequency}`,
        dueDay && `, due ${dueDay.charAt(0).toLowerCase()}${dueDay.slice(1)}`,
        rentPaidTo.trim() && ` and paid to ${rentPaidTo.trim()}`,
        this.agreement.roommates.length > 1 ? ". Each roommate pays:" : ".",
      ]
        .filter(Boolean)
        .join("");
    },

    get signatories() {
      const roommates = this.agreement.roommates.map((roommate, index) =>
        this.nameFor(roommate, index),
      );
      const landlord = this.agreement.landlord.trim();

      return landlord ? [`${landlord} (landlord)`, ...roommates] : roommates;
    },

    get clauses() {
      const { rules, additionalTerms } = this.agreement;

      return [
        "rent",
        "deposit",
        "bills",
        "furniture",
        ...Object.keys(rules).filter((rule) => rules[rule].enabled),
        "disagreements",
        ...(additionalTerms.trim() ? ["other"] : []),
      ];
    },

    heading(clause, title) {
      return `${this.clauses.indexOf(clause) + 1}. ${title}`;
    },

    sum(field) {
      return this.agreement.roommates.reduce(
        (total, roommate) => total + (Number(roommate[field]) || 0),
        0,
      );
    },

    nameFor(roommate, index) {
      if (roommate.name.trim()) {
        return roommate.name.trim();
      }

      return this.agreement.roommates.length > 1 ? `Roommate ${index + 1}` : "Roommate";
    },

    money(amount) {
      const value = Number(amount) || 0;

      return `${this.currencySymbol}${value.toLocaleString(undefined, {
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2,
      })}`;
    },

    date(value) {
      if (!value) {
        return "";
      }

      return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    },

    time(value) {
      if (!value) {
        return "";
      }

      const [hours, minutes] = value.split(":").map(Number);

      return new Date(2000, 0, 1, hours, minutes).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    },

    addRoommate() {
      this.agreement.roommates.push(blankRoommate());
    },

    removeRoommate(index) {
      if (this.agreement.roommates.length > 1) {
        this.agreement.roommates.splice(index, 1);
      }
    },

    reset() {
      if (confirm("Clear everything and start over?")) {
        this.agreement = defaults(this.defaultRentFrequency);
      }
    },

    print() {
      const printWindow = window.open("", "_blank");

      printWindow.document.write(`<!doctype html>
        <html>
          <head>
            <title>Roommate agreement</title>
            <style>
              body { font: 14px/1.6 system-ui, sans-serif; color: #111; max-width: 720px; margin: 40px auto; padding: 0 24px; }
              h2 { font-size: 22px; text-align: center; margin: 0 0 24px; }
              h3 { font-size: 15px; margin: 24px 0 4px; }
              p, li { margin: 0 0 8px; }
              ul { padding-left: 20px; }
              .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-top: 32px; }
              .signature { border-top: 1px solid #111; padding-top: 4px; margin-top: 48px; }
            </style>
          </head>
          <body>${this.$refs.preview.innerHTML}</body>
        </html>`);

      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    },
  };
}
